/**
 * OpsPage.tsx — 运营管理页（7 tab，对照 design/ops.jsx 1:1）
 *
 * 两轴：语言必填 × App（全部=该语言全局 / 某 App=可选覆盖）。
 * 无覆盖画空态 B，不把全局行画进 App 表。
 * 1:1 移植自原型 design/Novel-admin/src/pages/ops.jsx + ops-scope / ops-weekly / ops2。
 * 机械转换：JS→TS（加严格类型）、window.X 全局 → ESM import：
 *   - props { app, apps } → 当前 App 由 useAppScope().appId + useApps() 推导（appId==='all' → 聚合视图对象）；
 *   - window.NA_DATA.HOME_BLOCKS / BANNERS / FAQS / BOOKS → useHomeBlocks() / useBanners() / useFaqs() / useBooks()；
 *   - window.SYS_CFG.getEnabledLanguages() → useSysConfig()（真数据派生 enabled 语言数）；
 *   - window.toast → ~/ui/Toast 的 toast；
 *   - window.openDialog?.(name, props) → 本页本地 dialog open-state + 直接 import 弹窗组件；
 *     通用二次确认走 ~/ui/Dialog 的 ConfirmDialog（保留原型 warn / danger 三态）。
 *
 * 接真：区块 / Banner 位 / 轮播图写动作经 use-ops-mutations 落真实 API：
 *   - upsert（PUT）→ root/admin/operator；delete（DELETE）→ root/admin（对齐 server ops/index.ts requireRole）。
 *   - 上下架翻转 = 全 PUT 回传既有原始字段（title_i18n / reading_preferences / slug …）仅翻 is_active，避免 sparse 清空。
 *   - 删除系统预置（is_seed_baseline）全局栏 / Banner 位被拦（仅可上下架）。
 *   - FAQ canonical（#221）：REST CRUD——create(POST)/update(PATCH sparse)/delete(DELETE 软删) 均 root/admin/operator；
 *     分类 = faq_categories 可配置注册表，「新建分类」已接真、下拉候选取 useFaqCategories。系统预置 FAQ 禁删（仅下架）。
 *     NOT-in-scope（docs/plans/2026-07-01-faq-canonical.md）：分类改名/删除 UI、per-App(app_id) FAQ override UI、FAQ 复制。
 *     其余契约无端点动作（区块内书目管理 / Banner 样式预设·备注 / S006 初始化）disabled + title，禁假成功 toast。
 *
 * 接真（PR7 shelf+popup）：书架推荐 / 运营弹窗写动作经 use-ops-shelf-popup 落真实 API：
 *   - 书架推荐 upsert/toggle/reorder → PUT /ops/shelf-recommendations（root/admin/operator）；delete → DELETE /:id（root/admin）。
 *   - 运营弹窗 启停 → PATCH /ops/popup-scenes/:id；未建行上架先 POST 建行（均 root/admin）。
 *   - 上下架/启停翻转 = 全 PUT 回传既有字段仅翻 is_listed/is_active，避免 sparse 清空 novel_id/product_id。
 *   - 书架推荐「全部」= 该语言全局层，可新增 / 上下架 / 删除。
 *   - 运营弹窗 Paywall 落点（S008）→ GET /ops/popup-placements 按结算通道闭集逐行只读展示；配置与清空在
 *     「支付管理 › 支付页面 › 运营弹窗」（PUT /payment-pages/:id/paywalls 的 operational_popup 分支，A8 §7.5）。
 *   - NOT-in-scope（§五）：书架推荐弹窗内推荐语录入（走书籍编辑 ADM-BOK-S003）；运营弹窗场景名称 i18n（enum label 派生）。
 * 视觉层（JSX/className/inline-style）与原型逐字一致。
 */

import type { ShelfRecommendationResponse } from '@novel-admin/shared';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppScope } from '~/app/app-scope-store';

import {
  type AppRow,
  type Faq,
  type HomeBlock,
  useApps,
  useBanners,
  useFaqs,
  useHomeBlocks,
} from '~/data/queries';
import {
  type ShelfRecommendationMutations,
  useGlobalPopupLayerReaders,
  useOperationalPopupSceneMutations,
  useOperationalPopupScenes,
  useShelfRecommendationMutations,
  useShelfRecommendations,
} from '~/data/use-ops-shelf-popup';
import { langName } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { ConfirmDialog } from '~/ui/Dialog';
import { Section } from '~/ui/Section';
import { KpiSkeleton, SectionSkeleton } from '~/ui/Skeleton';
import { Table } from '~/ui/Table';
import { BannerSlidesTab, isAppDiscoverBanner, useBannerGlobalCount } from './BannerSlidesTab';
import { FaqsTab } from './FaqsTab';
import { OperationalPopupsTab } from './OperationalPopupsTab';
import { NoOverride } from './OpsEmptyStates';
import { OpsFillMatrix } from './OpsFillMatrix';
import {
  OpsCopyOverrideDialog,
  OpsDirectCreateOverrideDialog,
  OpsRemoveOverrideDialog,
} from './OpsOverrideDialogs';
import { type OpsConfigSource, OpsScopeBar } from './OpsScopeBar';
import { OpsSrcChip, deriveOpsSrc } from './OpsSrcChip';
import { PopupScopeBar } from './PopupScopeBar';
import { ShelfRecommendationsTab } from './ShelfRecommendationsTab';
import { TitleLangsDialog } from './TitleLangsDialog';
import { WeeklyFeaturedTab, useWeeklyGlobalCount } from './WeeklyFeaturedTab';
import { FAQCategoryEditor } from './dialogs/FAQCategoryEditor';
import { FAQEditor } from './dialogs/FAQEditor';
import { ShelfRecDialog } from './dialogs/ShelfRecDialog';
import {
  type OpsModule,
  type OpsScopeKind,
  deriveOpsScope,
  isOpsCopyLanguageAllowed,
  isOpsGlobal,
} from './ops-scope';
import {
  type OpsMutations,
  useCanDeleteOps,
  useCanUpsertOps,
  useCanWritePopupScenes,
  useOpsMutations,
} from './use-ops-mutations';

// ---------- 本页弹窗 open-state（取代原型 window.openDialog hub）----------
/** 通用二次确认弹窗配置（对应原型 openDialog('confirm', props)） */
interface ConfirmConfig {
  title: ReactNode;
  message: ReactNode;
  tone?: 'danger' | 'warn' | 'default';
  confirmLabel?: string;
  cancelLabel?: string;
  requireTyping?: string;
  onConfirm?: () => void;
}
/** 本页可打开的弹窗 */
type DialogState =
  | { kind: 'faq-editor'; faq?: Faq }
  | { kind: 'faq-category-editor' }
  | { kind: 'title-langs'; sectionName: string; sectionKey: string; block: HomeBlock }
  // 书架推荐 / 运营弹窗（PR7）：rec/scene 省略 = 新建，传入 = 编辑
  | { kind: 'shelf-rec'; rec?: ShelfRecommendationResponse; existingCount: number }
  | { kind: 'ops-copy'; module: OpsModule }
  | { kind: 'ops-direct-create'; module: OpsModule }
  | { kind: 'ops-remove'; module: OpsModule }
  | { kind: 'confirm'; config: ConfirmConfig }
  | null;

/** 当前 App 类型：聚合视图只用到 id / name / lang */
type CurrentApp = Pick<AppRow, 'id' | 'name' | 'lang'>;

export default function OpsPage() {
  const navigate = useNavigate();
  const { data: apps, isLoading: appsLoading } = useApps();
  const storeAppId = useAppScope((s) => s.appId);
  const [scopeAppId, setScopeAppId] = useState(storeAppId);
  const [lang, setLang] = useState('en');
  // 首屏主数据：App 列表（决定当前 App / 聚合态 / 视图徽章，全 5 tab 共用）+ 默认 tab「首页推荐」的
  // home-blocks（首次渲染 tab==='recommend' 时是该 tab 的 body 主数据源）。门控由真 TanStack Query
  // isLoading 驱动，不移植原型假定时器。
  //
  // 不做整页门控（codex P2 系统性）：页头 + tab bar + 各 tab 工具/按钮是页面 chrome，操作员任何时刻都该
  // 看得到、点得动——它们各用自己的 state/query，不依赖单一加载中的查询。整页 `return <PageSkeleton>`
  // 会在「某一个查询慢/挂起」时把整页（含其它独立 tab 与工具）一并扣住、困住操作员。改为：chrome 永远渲染，
  // 只在「依赖该加载查询的那个数据区」就地放骨架——首页推荐 tab 的区块列表区随 homeBlocksLoading 显骨架，
  // 其余 tab 各自按自己的 query 立即渲染。
  const { isLoading: homeBlocksLoading } = useHomeBlocks(scopeAppId, lang);

  // 当前 App：appId==='all' → 聚合视图对象；否则取选中 App。
  const selectedApp = apps.find((a) => a.id === scopeAppId);
  const app: CurrentApp =
    isOpsGlobal(scopeAppId) || !selectedApp ? { id: 'all', name: '全部', lang: [] } : selectedApp;

  const [tab, setTab] = useState('sections');
  const [dialog, setDialog] = useState<DialogState>(null);
  const showScopeBar = tab !== 'fill';
  const tabModule: OpsModule | null =
    tab === 'sections'
      ? 'home_sections'
      : tab === 'weekly'
        ? 'home_sections'
        : tab === 'shelf'
          ? 'shelf_recommendations'
          : tab === 'banner'
            ? 'banner_slides'
            : tab === 'faq'
              ? 'faq'
              : null;
  const { configSource: homeConfigSource, data: scopedHomeBlocks } = useHomeBlocks(
    scopeAppId,
    lang,
  );
  const { data: globalHomeBlocks } = useHomeBlocks('all', lang);
  const { data: bannersForOverride, configSource: bannerConfigSource } = useBanners(
    scopeAppId,
    lang,
  );
  const { configSource: shelfConfigSource, data: scopedShelf } = useShelfRecommendations(
    scopeAppId,
    lang,
  );
  const { data: shelfGlobal } = useShelfRecommendations('all', lang);
  const { configSource: faqConfigSource, data: scopedFaqs } = useFaqs(scopeAppId, lang);
  const { data: globalFaqs } = useFaqs('all', lang);
  const weeklyGlobalN = useWeeklyGlobalCount(lang);
  const bannerGlobalN = useBannerGlobalCount(lang);
  const serverHasOverride =
    tabModule === 'home_sections'
      ? homeConfigSource === 'APP_DEDICATED'
      : tab === 'weekly'
        ? homeConfigSource === 'APP_DEDICATED'
        : tabModule === 'banner_slides'
          ? bannerConfigSource === 'APP_DEDICATED'
          : tabModule === 'shelf_recommendations'
            ? shelfConfigSource === 'APP_DEDICATED'
            : tabModule === 'faq'
              ? faqConfigSource === 'APP_DEDICATED'
              : false;
  const scope: OpsScopeKind = tabModule
    ? deriveOpsScope(tabModule, app.id, lang, serverHasOverride)
    : 'GLOBAL';
  const languageAllowed = isOpsCopyLanguageAllowed(app.lang, lang, app.id);
  const globalN =
    tab === 'weekly'
      ? weeklyGlobalN
      : tabModule === 'home_sections'
        ? globalHomeBlocks.filter((b) => (b.titleI18n?.[lang] ?? '').trim()).length
        : tabModule === 'shelf_recommendations'
          ? shelfGlobal.length
          : tabModule === 'faq'
            ? globalFaqs.length
            : tabModule === 'banner_slides'
              ? bannerGlobalN
              : 0;
  const overrideN =
    tab === 'weekly'
      ? scopedHomeBlocks.filter((b) => b.sectionId === 'weekly_featured').length
      : tabModule === 'home_sections'
        ? scopedHomeBlocks.filter((b) => (b.titleI18n?.[lang] ?? '').trim()).length
        : tabModule === 'shelf_recommendations'
          ? scopedShelf.length
          : tabModule === 'faq'
            ? scopedFaqs.length
            : tabModule === 'banner_slides'
              ? bannersForOverride.filter((b) => isAppDiscoverBanner(b, app.id)).length
              : 0;

  // URL ?tab= 锚点（删除支付页弹窗「去清理」等外部入口跳 /ops?tab=popup）：
  // 落地/在页时切到指定 tab；一次性消费后即清参（replace，本地 tab state 仍是唯一 tab 真值）。
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  useEffect(() => {
    if (!tabParam) return;
    if (
      ['sections', 'recommend', 'weekly', 'shelf', 'banner', 'popup', 'faq', 'fill'].includes(
        tabParam,
      )
    ) {
      setTab(tabParam === 'recommend' ? 'sections' : tabParam);
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('tab');
        return next;
      },
      { replace: true },
    );
  }, [tabParam, setSearchParams]);
  const closeDialog = () => setDialog(null);
  const openBookDetails = (novelId: string) => {
    closeDialog();
    navigate(`/books?bookId=${encodeURIComponent(novelId)}`);
  };

  const mutations = useOpsMutations();
  const shelfMutations = useShelfRecommendationMutations(scopeAppId, lang);
  const popupMutations = useOperationalPopupSceneMutations(scopeAppId);
  // 与 OperationalPopupsTab 内是同一个 queryKey，TanStack Query 去重、不会多发一次请求
  const { configSource: popupConfigSource } = useOperationalPopupScenes(scopeAppId);
  // 全局层视图才逐 App 探层：其余 tab / App 视图下 enabled=false，不打这轮请求。
  const { readers: popupGlobalReaders, covered: popupCoveredApps } = useGlobalPopupLayerReaders(
    tab === 'popup' && isOpsGlobal(scopeAppId),
  );
  const canUpsert = useCanUpsertOps();
  const canWritePopupScenes = useCanWritePopupScenes();
  const canDelete = useCanDeleteOps();
  // 写动作 PUT body 从 query cache 实时取最新行（adversarial F1）+ App override scope 解析（F2）。
  const queryClient = useQueryClient();

  const tabs: { id: string; label: string; icon: keyof typeof Icons }[] = [
    { id: 'sections', label: '首页推荐分区标题', icon: 'IconLayout' },
    { id: 'weekly', label: '首页每周精选书籍', icon: 'IconStar' },
    { id: 'shelf', label: '书架推荐', icon: 'IconBook' },
    { id: 'banner', label: '首页 Banner', icon: 'IconImg' },
    { id: 'popup', label: '运营弹窗', icon: 'IconFlame' },
    { id: 'faq', label: 'FAQ', icon: 'IconInfo' },
    { id: 'fill', label: '自动补齐', icon: 'IconLanguages' },
  ];
  const source: OpsConfigSource =
    scope === 'GLOBAL' ? 'GLOBAL' : scope === 'OVERRIDE' ? 'APP_DEDICATED' : 'GLOBAL_INHERITED';

  return (
    <div className="page page-enter">
      <div className="page-header">
        <div>
          <h1 className="page-title">运营管理</h1>
          <div className="page-sub">
            每行运营数据必带语言 ·「全部」= 该语言的全局配置（<span className="mono">app_id</span>{' '}
            空）· 某 App = 可选覆盖
          </div>
        </div>
      </div>

      <div style={{ borderBottom: '1px solid var(--border)', display: 'flex' }}>
        {tabs.map((t) => {
          const Ic = Icons[t.icon];
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                border: 0,
                background: 'transparent',
                padding: '9px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                font: 'inherit',
                fontSize: 12.5,
                cursor: 'pointer',
                color: tab === t.id ? 'var(--fg-strong)' : 'var(--fg-muted)',
                fontWeight: tab === t.id ? 500 : 400,
                borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
                marginBottom: -1,
              }}
            >
              <Ic size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* 运营弹窗没有语言维度（A2 §5 头部），故走专属 bar：只有 App 层一轴 + config_source 状态。
          它也不进 tabModule —— tabModule 驱动的是 §0.4 复制 / §0.5 移除那套语言覆盖骨架，本模块
          契约明写不接受，挂上去会长出一个点了必 422 的「从全局复制」。 */}
      {tab === 'popup' && (
        <div style={{ marginTop: 0 }}>
          <PopupScopeBar
            appId={app.id}
            configSource={popupConfigSource}
            readers={popupGlobalReaders}
            covered={popupCoveredApps}
            onApp={setScopeAppId}
          />
        </div>
      )}

      {showScopeBar && tabModule && (
        <div style={{ marginTop: 0 }}>
          <OpsScopeBar
            module={tabModule}
            appId={app.id}
            lang={lang}
            onApp={setScopeAppId}
            onLang={setLang}
            serverHasOverride={serverHasOverride}
            globalN={globalN}
            languageAllowed={languageAllowed}
            overrideN={scope === 'OVERRIDE' ? overrideN : 0}
            onCopy={() => setDialog({ kind: 'ops-copy', module: tabModule })}
            onDirectCreate={() => setDialog({ kind: 'ops-direct-create', module: tabModule })}
            onRemove={() => setDialog({ kind: 'ops-remove', module: tabModule })}
          />
        </div>
      )}

      {/* tab 内容区：App 列表加载中（App scope 未定，全 tab 共用）→ 该数据区放区块骨架（KPI 信息条 +
          列表区同形），不扣住页头/tab bar；App 就绪后各 tab 各按自己的 query 渲染。首页推荐 tab 再叠一层
          自身 homeBlocksLoading 骨架（其它 tab 与该 query 无关、不受其拖慢）。 */}
      {appsLoading ? (
        <div className="vstack" style={{ gap: 12 }}>
          <KpiSkeleton count={1} />
          <SectionSkeleton rows={5} />
        </div>
      ) : (
        <>
          {tabModule && scope === 'NO_OVERRIDE' && (
            <div style={{ marginTop: 12 }}>
              <NoOverride
                module={tabModule}
                appName={app.name}
                lang={lang}
                globalN={globalN}
                languageAllowed={languageAllowed}
                onCopy={() => setDialog({ kind: 'ops-copy', module: tabModule })}
                onDirectCreate={() => setDialog({ kind: 'ops-direct-create', module: tabModule })}
              />
            </div>
          )}
          {!(tabModule && scope === 'NO_OVERRIDE') &&
            tab === 'sections' &&
            (homeBlocksLoading ? (
              <div className="vstack" style={{ gap: 12 }}>
                <KpiSkeleton count={1} />
                <SectionSkeleton rows={5} />
              </div>
            ) : (
              <RecommendBlocks app={app} lang={lang} source={source} setDialog={setDialog} />
            ))}
          {!(tabModule && scope === 'NO_OVERRIDE' && tabModule !== 'faq') && tab === 'weekly' && (
            <WeeklyFeaturedTab appId={app.id} lang={lang} scope={scope} />
          )}
          {!(tabModule && scope === 'NO_OVERRIDE' && tabModule !== 'faq') && tab === 'shelf' && (
            <ShelfRecommendationsTab
              appId={app.id}
              appName={app.name}
              lang={lang}
              scope={scope}
              mutations={shelfMutations}
              canUpsert={canUpsert}
              canDelete={canDelete}
              onEdit={(rec, existingCount) =>
                setDialog({ kind: 'shelf-rec', rec, existingCount: existingCount ?? 0 })
              }
              onConfirm={(config) => setDialog({ kind: 'confirm', config })}
            />
          )}
          {!(tabModule && scope === 'NO_OVERRIDE' && tabModule !== 'faq') && tab === 'banner' && (
            <BannerSlidesTab
              appId={app.id}
              lang={lang}
              mutations={mutations}
              canUpsert={canUpsert}
              canDelete={canDelete}
              queryClient={queryClient}
              onConfirm={(config) => setDialog({ kind: 'confirm', config })}
            />
          )}
          {tab === 'popup' && (
            <OperationalPopupsTab
              appId={app.id}
              mutations={popupMutations}
              canUpsert={canWritePopupScenes}
              onConfirm={(config) => setDialog({ kind: 'confirm', config })}
            />
          )}
          {!(tabModule && scope === 'NO_OVERRIDE') && tab === 'faq' && (
            <FaqsTab
              appId={app.id}
              lang={lang}
              scope={scope}
              canUpsert={canUpsert}
              mutations={mutations}
              onEdit={(faq) => setDialog({ kind: 'faq-editor', faq })}
              onManageCats={() => setDialog({ kind: 'faq-category-editor' })}
              onConfirm={(config) => setDialog({ kind: 'confirm', config })}
            />
          )}
          {tab === 'fill' && <OpsFillMatrix />}
        </>
      )}

      {renderDialog(
        dialog,
        closeDialog,
        openBookDetails,
        app,
        lang,
        globalN,
        languageAllowed,
        overrideN,
        mutations,
        shelfMutations,
        canUpsert,
      )}
    </div>
  );
}

// ---------- 弹窗渲染（取代原型 DialogHub）----------
function renderDialog(
  dialog: DialogState,
  closeDialog: () => void,
  onOpenBookDetails: (novelId: string) => void,
  app: CurrentApp,
  lang: string,
  globalN: number,
  languageAllowed: boolean,
  overrideN: number,
  mutations: OpsMutations,
  shelfMutations: ShelfRecommendationMutations,
  canUpsert: boolean,
): ReactNode {
  if (!dialog) return null;
  switch (dialog.kind) {
    case 'faq-editor':
      // canUpsert 传进 FAQEditor（adversarial F3）：会话内角色降级时弹窗 save 仍被禁，防发被拒写。
      return (
        <FAQEditor
          faq={dialog.faq}
          mutations={mutations}
          canUpsert={canUpsert}
          appId={app.id}
          lang={lang}
          onClose={closeDialog}
        />
      );
    case 'faq-category-editor':
      // 新建 FAQ 分类（#221 可配置注册表）；canUpsert 门禁同 FAQ 写（root/admin/operator）。
      return (
        <FAQCategoryEditor mutations={mutations} canUpsert={canUpsert} onClose={closeDialog} />
      );
    case 'title-langs':
      return (
        <TitleLangsDialog
          sectionName={dialog.sectionName}
          sectionKey={dialog.sectionKey}
          block={dialog.block}
          appId={app.id}
          lang={lang}
          mutations={mutations}
          onClose={closeDialog}
        />
      );
    case 'shelf-rec':
      // canUpsert 传进（会话内降级时 save 仍被禁，防发被拒 PUT）；existingCount 用于新建排到末尾 sort_order。
      return (
        <ShelfRecDialog
          rec={dialog.rec}
          app={app}
          lang={lang}
          existingCount={dialog.existingCount}
          mutations={shelfMutations}
          canUpsert={canUpsert}
          onClose={closeDialog}
          onOpenBookDetails={onOpenBookDetails}
        />
      );
    case 'ops-copy':
      return (
        <OpsCopyOverrideDialog
          module={dialog.module}
          appName={app.name}
          appId={app.id}
          lang={lang}
          globalN={globalN}
          languageAllowed={languageAllowed}
          onClose={closeDialog}
        />
      );
    case 'ops-direct-create':
      return (
        <OpsDirectCreateOverrideDialog
          module={dialog.module}
          appName={app.name}
          appId={app.id}
          lang={lang}
          languageAllowed={languageAllowed}
          onClose={closeDialog}
        />
      );
    case 'ops-remove':
      return (
        <OpsRemoveOverrideDialog
          module={dialog.module}
          appName={app.name}
          appId={app.id}
          lang={lang}
          overrideN={overrideN}
          onClose={closeDialog}
        />
      );
    case 'confirm': {
      const c = dialog.config;
      return (
        <ConfirmDialog
          title={c.title}
          message={c.message}
          tone={c.tone}
          confirmLabel={c.confirmLabel}
          cancelLabel={c.cancelLabel}
          requireTyping={c.requireTyping}
          onConfirm={c.onConfirm ?? (() => {})}
          onClose={closeDialog}
        />
      );
    }
    default:
      return null;
  }
}

// =========================================================
// 首页推荐区块 tab
// =========================================================
function RecommendBlocks({
  app,
  lang,
  source,
  setDialog,
}: {
  app: CurrentApp;
  lang: string;
  source: OpsConfigSource;
  setDialog: (s: DialogState) => void;
}) {
  const { data: homeBlocks } = useHomeBlocks(app.id, lang);

  const SEED_KEYS = [
    { key: 'weekly_featured', name: '每周精选', note: '书单在「首页每周精选书籍」Tab 维护' },
    { key: 'for_you', name: '猜你喜欢', note: '书单由策略产出，不在本台维护' },
    { key: 'new_releases', name: '新书上架', note: '书单由策略产出，不在本台维护' },
  ] as const;
  const titleRows = SEED_KEYS.map((s) => {
    const b = homeBlocks.find((x) => x.sectionId === s.key || x.strategyKey === s.key);
    const text = b?.titleI18n?.[lang]?.trim() ?? '';
    return { ...s, block: b, text };
  });
  return (
    <div className="vstack" style={{ gap: 12 }}>
      <Section
        pad="4px 18px 16px"
        title={`分区标题 · ${lang.toUpperCase()} ${langName(lang)}`}
        sub={`系统固定 3 个分区（每周精选 / 猜你喜欢 / 新书上架）· 每个语言 Tab 对应独立行 · ${source === 'GLOBAL' ? '当前语言的全局配置' : '覆盖只维护当前语言'}`}
      >
        <Table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 150 }}>分区名</th>
              <th>{lang.toUpperCase()} 标题</th>
              <th style={{ width: 100 }}>状态</th>
              <th style={{ width: 100 }}>布局</th>
              <th style={{ width: 96 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {titleRows.map((r) => {
              const b = r.block;
              const open = () =>
                b &&
                setDialog({
                  kind: 'title-langs',
                  sectionName: r.name,
                  sectionKey: r.key,
                  block: b,
                });
              return (
                <tr key={r.key} style={{ cursor: b ? 'pointer' : undefined }} onClick={open}>
                  <td>
                    <div style={{ fontSize: 12.5, fontWeight: 500 }}>{r.name}</div>
                    <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                      {r.key}
                    </div>
                  </td>
                  <td style={{ fontSize: 12.5, color: r.text ? 'var(--fg)' : 'var(--fg-faint)' }}>
                    {r.text || (
                      <span className="hstack" style={{ gap: 4 }}>
                        <Icons.IconAlert size={10} style={{ color: 'var(--warn)' }} />
                        该语言未译 · 接口不返回
                      </span>
                    )}
                    <div style={{ fontSize: 10.5, color: 'var(--fg-faint)', marginTop: 2 }}>
                      {r.note}
                    </div>
                  </td>
                  <td>
                    <OpsSrcChip src={deriveOpsSrc(lang, r.text)} />
                  </td>
                  <td>{b ? (b.layout === 'VERTICAL' ? '竖排 · 5 本' : '横排 · 10 本') : '—'}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={!b}
                      onClick={() =>
                        b &&
                        setDialog({
                          kind: 'title-langs',
                          sectionName: r.name,
                          sectionKey: r.key,
                          block: b,
                        })
                      }
                    >
                      <Icons.IconLanguages size={11} />
                      编辑 {lang.toUpperCase()} 标题
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 10, lineHeight: 1.6 }}>
          点行编辑当前语言标题；切换顶部语言 Tab
          可维护其它语言。「猜你喜欢」/「新书上架」的书单由策略产出，不在本台维护；「每周精选」的书单在下一个
          Tab。
        </div>
      </Section>
    </div>
  );
}
